import type { Logger } from "@expert/logging";
import type { GaiaBotMessage, ReactiveMessagePayload } from "@expert/shared-types";

import { reactAnalytics } from "@asurion-core/eventualize-react";
import { getSessionType } from "@expert/gaia";
import { trackAppcuesEvent, trackFullStoryEvent } from "@expert/monitoring";
import { getCitationAnalytics, processMessage, setDeviceInfo } from "@expert/solve-tools";

import type { ExpertAssistMessage } from "../shared-types";

import { addFailureMessage, addMessage } from "../state";

export const handleIncomingMessage = (msg: GaiaBotMessage, logger: Logger) => {
    const { sessionId, callSid, chatId, formattedMessage, analytics } = parseExpertAssistMessage(msg);

    if (formattedMessage && !formattedMessage.text?.trim()) {
        // A solicited Search reply that comes back empty must still complete the request *visibly*: the
        // orchestrator-invoke-success event has already cancelled the timeout and cleared the expert
        // message's retry state, so with no bubble the timeline would show a loading spinner forever
        // (generateTimelineItems treats a trailing expert message with no retryPayload as still loading).
        // Surface a bot-failure instead. Unsolicited proactive guidance has no pending request behind it,
        // so an empty one is still dropped quietly rather than rendering a blank bubble.
        if (formattedMessage.type === "Search" && sessionId) {
            logger.info({ sessionId }, "GAIA | empty Search reply - surfacing a bot failure instead of a blank bubble");
            addFailureMessage(sessionId, "botFailure");
            return;
        }
        logger.info(
            { sessionId: sessionId ?? "undefined", messageType: formattedMessage.type },
            "GAIA | dropping empty expert-assist message",
        );
        return;
    }

    if (sessionId && formattedMessage) {
        addMessage(sessionId, formattedMessage);
        const reactiveMessage = msg as ReactiveMessagePayload;
        setDeviceInfo(
            {
                sessionId,
                externalDevices: reactiveMessage.externalDevices,
                detectedDevices: reactiveMessage.devices,
                origin: "incidental",
            },
            logger,
        );
        const identities = {
            ...(callSid && { CallSid: callSid }),
            ...(sessionId && { SessionId: sessionId }),
            ...(chatId && { ChatId: chatId }),
        };

        // Proactive guidance has its own received event (matching Guide_/AutoComplete_SuggestionReceived),
        // so it reports under that name in place of the generic MessageReceived rather than double-counting
        // the receipt.
        const isGuidance = formattedMessage.type === "ProactiveGuidance";
        const businessEvent = isGuidance ? "ProactiveGuidance_SuggestionReceived" : "MessageReceived";
        const monitoringEvent = isGuidance ? "ProactiveGuidance_SuggestionReceived" : "ExpertAssistMessageReceived";

        void reactAnalytics.dispatcher.withIdentities(identities).dispatchBusinessEvent(businessEvent, {
            senderType: "bot",
            messageType: formattedMessage.type,
            messageId: formattedMessage.id,
            ...analytics,
        });
        trackFullStoryEvent(monitoringEvent, {
            sessionId,
            callSid,
            chatId,
            messageType: formattedMessage.type,
            ...analytics,
        });
        trackAppcuesEvent(monitoringEvent, { messageType: formattedMessage.type, ...analytics });
    } else {
        addFailureMessage(sessionId, "botFailure");
        logger.warn(
            { sessionId: sessionId ?? "undefined", hasFormattedMessage: !!formattedMessage, fullMessage: msg },
            "GAIA | Unable to handling incoming message - missing expected value",
        );
    }
};

export const parseExpertAssistMessage = (msg: GaiaBotMessage) => {
    let formattedMessage: ExpertAssistMessage | undefined;
    let analytics: Record<string, unknown> | undefined;

    const sessionId = msg.sessionGroupId;

    const sessionType = getSessionType(sessionId);
    const isChat = sessionType === "chat";
    const chatId = isChat ? msg.sessionId : undefined;
    const callSid = sessionType === "voice" && msg.sessionId?.startsWith("C") ? msg.sessionId : undefined; // TODO: This only works for twilio call sids

    if ("response" in msg) {
        const messageId = crypto.randomUUID();
        const { response, analytics: updatedAnalytics } = processMessage({ ...msg, messageId });
        analytics = updatedAnalytics;
        formattedMessage = {
            id: messageId,
            text: response,
            type: "Search",
            isUnread: true,
            status: "active",
            timestamp: msg.responseTimestamp,
            citations: msg.citations,
        };
    } else if (msg.suggestion.messageType === "Proactive") {
        const isGuidance = msg.suggestion.producer === "proactive-guidance";
        formattedMessage = {
            id: msg.suggestion.messageId,
            text: msg.suggestion.message,
            type: isGuidance ? "ProactiveGuidance" : msg.suggestion.messageType,
            isUnread: !isGuidance,
            status: "active",
            timestamp: msg.suggestion.outputTimestamp,
            tag: isGuidance ? "Suggested guidance" : "Solve suggestion",
            citations: msg.suggestion.citations,
            metadata: isGuidance ? { aiQuickActions: msg.suggestion.quickActions } : undefined,
        };
        analytics = {
            messageIntent: msg.suggestion.metadata?.intentClassification ?? "unknown",
            ...(msg.suggestion.producer && { producer: msg.suggestion.producer }),
        };
    } else if (msg.suggestion.messageType === "Search") {
        // Reactive response routed through the proactive-guidance app. Render it inline in the
        // timeline like a standard reactive expert-assist message rather than in the sticky bubble.
        formattedMessage = {
            id: msg.suggestion.messageId,
            text: msg.suggestion.message,
            type: "Search",
            isUnread: true,
            status: "active",
            timestamp: msg.suggestion.outputTimestamp,
            citations: msg.suggestion.citations,
            // Surface any AI-suggested follow-ups inline, the same way the sticky guidance bubble does.
            metadata: msg.suggestion.quickActions ? { aiQuickActions: msg.suggestion.quickActions } : undefined,
        };
        analytics = {
            messageIntent: msg.suggestion.metadata?.intentClassification ?? "unknown",
            ...(msg.suggestion.producer && { producer: msg.suggestion.producer }),
            ...getCitationAnalytics(msg.suggestion.citations),
        };
    }

    return { formattedMessage, sessionId, callSid, chatId, analytics };
};
